import React from 'react';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';

type ChartTooltipPayloadEntry = {
  value: number | string;
  name?: string;
  color?: string;
  dataKey?: string | number;
  payload?: unknown;
};

export type ChartTooltipProps = {
  /** Provided by recharts when cursor is over the chart. */
  active?: boolean;
  /** Provided by recharts. Array of series values at the hovered point. */
  payload?: ChartTooltipPayloadEntry[];
  /** Provided by recharts. The x-axis category (date string, category name) at the hovered point. */
  label?: string | number;

  /**
   * Format the label shown at the top of the tooltip.
   *   - If unset and label parses as ISO datetime → `Mon, 13 Jul 2026`.
   *   - If unset and label is anything else → shown as-is.
   *   - Set to `null` to hide the header entirely (useful for pie charts).
   */
  labelFormatter?: ((label: string | number) => string) | null;

  /** Format each series' numeric value. Defaults to `value.toLocaleString()`. */
  valueFormatter?: (value: number | string, name?: string) => string;

  /** Map from series `dataKey` to a human-readable display name. */
  nameMap?: Record<string, string>;

  /** Show a Total row summing all series' values. Useful for stacked bar charts. */
  showTotal?: boolean;

  /** Label for the Total row when `showTotal` is true. */
  totalLabel?: string;

  /** Hide series whose value is zero/empty. */
  filterZero?: boolean;

  /** Override the max width. */
  minWidth?: number;
};

const formatDefaultLabel = (label: string | number): string => {
  if (typeof label === 'number') return String(label);
  const dt = DateTime.fromISO(label);
  return dt.isValid ? dt.toFormat('ccc, dd LLL yyyy') : label;
};

const defaultValueFormat = (value: number | string): string => {
  if (typeof value === 'number') return value.toLocaleString();
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber.toLocaleString() : String(value);
};

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
  nameMap,
  showTotal = false,
  totalLabel = 'Total',
  filterZero = false,
  minWidth = 180
}) => {
  if (!active || !payload || !payload.length) return null;

  const visibleRows = filterZero
    ? payload.filter((p) => {
        const n = Number(p.value);
        return Number.isFinite(n) ? n !== 0 : !!p.value;
      })
    : payload;

  if (!visibleRows.length) return null;

  const formatValue = valueFormatter ?? defaultValueFormat;

  const resolvedNameFor = (p: ChartTooltipPayloadEntry): string => {
    const key = typeof p.dataKey === 'string' ? p.dataKey : undefined;
    if (key && nameMap && nameMap[key]) return nameMap[key];
    return p.name ?? key ?? '';
  };

  const showHeader = labelFormatter !== null && label !== undefined && label !== null && label !== '';
  const headerText =
    showHeader && label !== undefined
      ? (labelFormatter ?? formatDefaultLabel)(label as string | number)
      : '';

  const total = showTotal
    ? visibleRows.reduce((sum, p) => {
        const n = Number(p.value);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0)
    : 0;

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid rgba(43, 34, 88, 0.08)',
        boxShadow:
          '0 1px 2px rgba(43, 34, 88, 0.05), 0 8px 24px rgba(43, 34, 88, 0.08)',
        padding: '10px 14px',
        minWidth,
        fontFamily: 'Montserrat',
        pointerEvents: 'none'
      }}
    >
      {showHeader && (
        <Typography
          sx={{
            fontSize: 10,
            color: '#7C7C7C',
            fontWeight: 600,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            mb: 0.75
          }}
        >
          {headerText}
        </Typography>
      )}

      {visibleRows.map((p, i) => (
        <Box
          key={p.dataKey ?? p.name ?? i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: i === 0 ? 0 : 0.5
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: p.color ?? '#086BDE',
              flexShrink: 0,
              boxShadow: `0 0 0 2px ${(p.color ?? '#086BDE')}22`
            }}
          />
          <Typography sx={{ fontSize: 13, color: '#333', flex: 1, fontWeight: 500 }}>
            {resolvedNameFor(p)}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1f1f1f', ml: 1.5 }}>
            {formatValue(p.value, resolvedNameFor(p))}
          </Typography>
        </Box>
      ))}

      {showTotal && visibleRows.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1,
            pt: 1,
            borderTop: '1px solid rgba(43, 34, 88, 0.08)'
          }}
        >
          <Typography sx={{ fontSize: 12, color: '#7C7C7C', flex: 1, fontWeight: 600 }}>
            {totalLabel}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1f1f1f' }}>
            {formatValue(total)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChartTooltip;
